import { ERROR_MESSAGES } from '@/constants/events';
import { isStatusError } from '@/utils/networkErrors';

/**
 * Diner-facing copy for a delivery-coverage check that did not produce a real
 * answer (TEAM-14744).
 *
 * Three outcomes have to read differently, and before this module two of them
 * did not:
 *
 * 1. The check **completed** and said no location delivers there — brand-
 *    configurable, still read from `locations.delivery_search_no_results_text`.
 *    Not defined here.
 * 2. The check **itself failed** — coo-api answers HTTP 502
 *    `delivery_coverage_check_failed`. Nothing is known about the address, so
 *    the copy must not imply anything is wrong with it.
 * 3. Anything else threw — the address could not be confirmed, which *may* be
 *    the address's fault, so pointing the diner at it is fair.
 *
 * Hardcoded rather than routed through `StringAccessor`, matching
 * `features/store/pos-unavailable/constants.ts`.
 *
 * @see docs/DINER_ERROR_COPY.md
 */
export const COVERAGE_ERROR_COPY = {
  /** Outcome 2. The check never got far enough to have an opinion about the address. */
  checkFailed:
    "We couldn't check delivery for this address right now. Please try again, or switch to pickup.",
  /** Outcome 3. The address is a plausible cause, so pointing at it is fair. */
  addressUnconfirmed:
    "We couldn't confirm this delivery address. Please double-check it, or switch to pickup.",
} as const;

/**
 * Local, not the retry layer's `GATEWAY_STATUSES`: this is coo-api's
 * `delivery_coverage_check_failed` contract, not a retry policy. The two are equal
 * today by coincidence and must stay free to diverge.
 */
const COVERAGE_CHECK_FAILED_STATUSES: readonly number[] = [502];

/**
 * True for any 502 from a coverage call, which is how coo-api signals
 * `delivery_coverage_check_failed`. The body is not inspected, so a 502 raised
 * in front of coo-api (CDN, load balancer) matches too — correct either way,
 * since neither case knows anything about the address.
 */
export const isCoverageCheckFailure = (error: unknown): boolean =>
  isStatusError(error, COVERAGE_CHECK_FAILED_STATUSES);

/**
 * The message a diner reads after a coverage check throws. Single entry point,
 * so the call sites cannot drift apart (AC #9).
 */
export const coverageErrorMessage = (error: unknown): string =>
  isCoverageCheckFailure(error)
    ? COVERAGE_ERROR_COPY.checkFailed
    : COVERAGE_ERROR_COPY.addressUnconfirmed;

/** The Sentry event name for the same throw, so failed checks stay countable apart. */
export const coverageErrorEventName = (error: unknown): string =>
  isCoverageCheckFailure(error)
    ? ERROR_MESSAGES.DELIVERY_COVERAGE_CHECK_FAILED
    : ERROR_MESSAGES.VALIDATE_DELIVERY_ADDRESS_ERROR;
